import { Button } from '@/components/ui/button';
import { CameraDto } from '@/lib/api/types.gen';
import { Controller, useWatch } from 'react-hook-form';
import { useAddDeviceFormCtx } from './AddDeviceFormProvider';
import { DeviceSnapshot } from '@/components/device/DeviceSnapshot';
import { cn } from '@/lib/utils';
import { useEffect, useMemo, useState } from 'react';
import { useDeviceFilterToggle } from '@/lib/devices/toggle';
import { Input } from '@/components/ui/input';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 10;

export const DeviceSelectTable = ({ subDevices }: { subDevices: CameraDto[] }) => {
  const form = useAddDeviceFormCtx();
  const monitored = useWatch({ control: form.control, name: 'monitoredDevices' });
  const [page, setPage] = useState(0);

  const devicesWithFormMonitoring = useMemo(() => {
    return subDevices.map((x) => ({
      ...x,
      monitored: x.id != null ? (monitored?.[x.id] ?? true) : true,
    }));
  }, [monitored, subDevices]);

  const cameraToggle = useDeviceFilterToggle({
    cameras: devicesWithFormMonitoring,
    onToggle(cameraIds, enabled) {
      if (enabled) {
        const values = { ...form.getValues('monitoredDevices') };
        for (const id of cameraIds) {
          values[id] = true;
        }
        form.setValue('monitoredDevices', values, { shouldDirty: true });
        return;
      }

      const values = { ...form.getValues('monitoredDevices') };
      for (const id of cameraIds) {
        values[id] = false;
      }
      form.setValue('monitoredDevices', values, { shouldDirty: true });
    },
  });

  // Reset to page 0 when search/filter changes
  useEffect(() => {
    setPage(0);
  }, [cameraToggle.searchQuery]);

  const totalFiltered = cameraToggle.filteredCameras.length;
  const totalPages = Math.ceil(totalFiltered / PAGE_SIZE);
  const needsPagination = totalFiltered > PAGE_SIZE;
  const pagedCameras = needsPagination
    ? cameraToggle.filteredCameras.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    : cameraToggle.filteredCameras;

  return (
    <div className="border rounded-xl bg-card2" id="available-feeds">
      <div className="w-full items-center justify-between p-6 py-4 border-b flex">
        <h3 className="text-xl font-semibold">Available Feeds</h3>
        <div className="flex items-center justify-between gap-2">
          <Input
            placeholder="Search feeds..."
            value={cameraToggle.searchQuery}
            onChange={(e) => cameraToggle.setSearchQuery(e.target.value)}
            className="w-max"
            isSearch
          />
          <Button variant="outline" onClick={cameraToggle.onToggleFilteredCameras} className="w-fit whitespace-nowrap">
            {cameraToggle.selectMode === 'select' ? 'Select' : 'Deselect'}
            {cameraToggle.isFiltering
              ? ` ${cameraToggle.filteredCameras.length} filtered camera${cameraToggle.filteredCameras.length === 1 ? '' : 's'}`
              : ' all cameras'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-4">
        {pagedCameras.map((device, i) => {
          return (
            <Controller
              control={form.control}
              name={`monitoredDevices.${device.id}`}
              defaultValue={true}
              key={`${device.id ?? String(i)}`}
              render={({ field }) => {
                return (
                  <div
                    role="button"
                    onClick={() => field.onChange(!field.value)}
                    className={cn(
                      'relative cursor-pointer rounded-lg border p-2 transition-all',
                      field.value
                        ? 'border-lime/50 bg-lime/5'
                        : 'border-white/10 opacity-50 grayscale'
                    )}
                  >
                    <div className="aspect-video w-full overflow-hidden rounded-md bg-black/20">
                      <DeviceSnapshot
                        className="h-full w-full object-cover"
                        deviceId={device.id}
                        withLoadingText={false}
                      />
                    </div>
                    <div className="mt-2">
                      <span className="truncate text-xs font-medium block">{device.name}</span>
                    </div>
                  </div>
                );
              }}
            />
          );
        })}
      </div>

      {needsPagination && (
        <div className="flex items-center justify-between border-t px-4 py-3">
          <span className="text-sm text-gray-400">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalFiltered)} of {totalFiltered} cameras
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-gray-400 min-w-[80px] text-center">
              Page {page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
